import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';
import {
  ApplicationForm,
  IApplicationForm,
  ICreateApplicationFormParams,
  IGlobalSDK,
  IHttpResponse,
  IPartnerEngineConfig,
  SdkKeys,
  getItem,
  getPartnerEngineConfig,
  loadSingleApplicationForm,
  loadSingleApplicationFormFromToken,
} from '@boldpenguin/sdk';
import { BehaviorSubject, Observable, from, of, throwError } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import {
  IAuthControllerServiceClientApplication,
  IAuthGetTokenResponse,
  IGuestFlowApplicationResponse,
} from '../models/guest-flow-response.model';
import { AuthenticationService } from './authentication.service';

// Ivy requires this to be defined here
declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Window {
    BpSdk?: IGlobalSDK;
  }
}

@Injectable({
  providedIn: 'root',
})
export class ApplicationFormService {
  loadingApplicationForm$ = new BehaviorSubject<boolean>(false);
  applicationFormId: string | undefined;

  constructor(
    private auth: AuthenticationService,
    @Optional() private http: HttpClient,
  ) {}

  get store(): IGlobalSDK['store'] | undefined {
    if (window?.BpSdk?.store) {
      return window.BpSdk.store;
    }
    return undefined;
  }

  get peConfig(): IPartnerEngineConfig | undefined {
    if (this.store) {
      const {
        config: { env, uri },
      } = this.store.getState();
      if (uri === '') {
        return undefined;
      }
      return {
        token: this.auth.token ?? '',
        env,
        uri,
      };
    }
    return undefined;
  }

  newApplicationForm(
    quoteStartBody?: ICreateApplicationFormParams | undefined,
    loadAfterCreate = true,
  ): Observable<string> {
    this.loadingApplicationForm$.next(true);
    if (!this.peConfig) {
      return throwError('Sdk not initialized');
    }
    let app = {};
    if (quoteStartBody) {
      app = quoteStartBody;
    }
    return from(
      ApplicationForm.createApplicationForm(
        app,
        this.peConfig as IPartnerEngineConfig,
      ),
    ).pipe(
      map((resp: IHttpResponse<IApplicationForm>) => {
        const {
          parsedBody: { id },
        } = resp;
        if (loadAfterCreate) {
          this.loadSingleApplicationForm(id);
        }
        this.loadingApplicationForm$.next(false);
        return id;
      }),
      catchError(err => {
        return throwError(err);
      }),
    );
  }

  loadSingleApplicationForm(id: string): void {
    this.applicationFormId = id;
    if (this.store) {
      this.store.dispatch(
        loadSingleApplicationForm({
          config: this.peConfig as IPartnerEngineConfig,
          id,
        }),
      );
    }
  }

  loadGuestApplicationForm(): Observable<undefined | string> {
    if (!this.store || !this.peConfig) {
      return throwError('Sdk not initialized');
    }
    const token = getItem(SdkKeys.token);
    const token_id = getItem(SdkKeys.cableToken);
    if (!!token && !!token_id) {
      const configWithTokens = { ...this.peConfig, ...{ token, token_id } };
      this.store.dispatch(loadSingleApplicationFormFromToken(configWithTokens));
    }
    return of();
  }

  getBcsFilters(search: string): Observable<string[]> {
    if (!this.store) {
      return throwError('Sdk not initialized');
    }
    const { uri } = getPartnerEngineConfig(this.store.getState());
    const url = `${uri}/business_type_search/autocomplete?filter=${search}`;
    const headers = new HttpHeaders({ authorization: this.auth.authorization });
    return this.http
      .get<{ value: string }[]>(url, { headers, withCredentials: true })
      .pipe(map((results: { value: string }[]) => results.map(r => r.value)));
  }

  createGuestFlowApplication(
    authUri: string,
    apiUri: string,
    clientId: string,
    clientSecret: string,
    answerValues?: { code: string; answer: string | number }[],
  ): Observable<IGuestFlowApplicationResponse> {
    const application: IAuthControllerServiceClientApplication = {
      application_form: {
        create_guest_user: true,
        answer_values: [],
      },
    };

    if (answerValues) {
      application.application_form.answer_values = answerValues;
    }

    return this.getAuthToken(authUri, clientId, clientSecret).pipe(
      mergeMap(res =>
        this.newGuestApplicationForm(
          apiUri,
          res.extra.raw_info.tenant_id,
          res.access_token,
          application,
        ),
      ),
    );
  }

  copyApplication(id: string): Observable<HttpResponse<void>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    const uri = `${window.BpSdk?.getConfig().uri}/copies/${id}/create_copy`;
    return this.http.post<void>(
      uri,
      {},
      { headers, observe: 'response', withCredentials: true },
    );
  }

  private newGuestApplicationForm(
    apiUri: string,
    tenantId: string,
    token: string,
    application: IAuthControllerServiceClientApplication,
  ): Observable<IGuestFlowApplicationResponse> {
    const url = `${apiUri}/tenants/${tenantId}/application_forms`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    return this.http.post<IGuestFlowApplicationResponse>(url, application, {
      headers,
      withCredentials: true,
    });
  }

  private getAuthToken(
    authUrl: string,
    clientId: string,
    clientSecret: string,
  ): Observable<IAuthGetTokenResponse> {
    const url = `${authUrl}/auth/token?grant_type=client_credentials`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Basic ${btoa(clientId + ':' + clientSecret)}`,
    });
    return this.http.post<IAuthGetTokenResponse>(url, null, { headers });
  }
}
